/**
 * ReadyStep - Final step of onboarding
 * PRD: PRD-onboarding-organisateur.md
 */

import React from 'react';
import { useLocation } from 'wouter';
import { Button, Icon } from '../../../../pages/DesignSystem';
import { useIsDesktop } from '../../../../hooks/useMediaQuery';

interface ReadyStepProps {
  // 🔧 FIX (item 1 - PRD robustesse réseau faible): onComplete résout un booléen
  // de succès réel (au lieu de toujours résoudre). true = l'appel serveur a
  // vraiment réussi, false = échec (déjà notifié via toast par le parent).
  onComplete: () => Promise<boolean>;
  isSubmitting: boolean;
}

const ReadyStep: React.FC<ReadyStepProps> = ({ onComplete, isSubmitting }) => {
  const [, setLocation] = useLocation();
  const isDesktop = useIsDesktop();

  // Handle completion - only go to dashboard if the server call actually
  // succeeded. On failure we stay on this step so the user can retry (the
  // error toast is already shown by OnboardingModal.handleComplete).
  const handleComplete = async () => {
    const success = await onComplete();
    if (success) {
      setLocation('/organizer/dashboard');
    }
  };

  return (
    <div className="onboarding-step onboarding-step--ready">
      {/* Illustration */}
      <div className="onboarding-step__illustration">
        <div className="onboarding-step__icon-wrapper onboarding-step__icon-wrapper--success">
          <Icon name="CheckCircle" size={48} />
        </div>
      </div>

      {/* Content */}
      <div className="onboarding-step__content">
        <h1 className="onboarding-step__title">You're all set!</h1>

        <p className="onboarding-step__description">
          Your profile is live! Tourists can already discover your events
        </p>

        <div className="onboarding-step__next-steps">
          <h3 className="onboarding-step__next-title">What's next?</h3>
          <ul className="onboarding-step__next-list">
            <li>
              <Icon name="CalendarPlus" size={16} />
              Create your first event
            </li>
            <li>
              <Icon name="Upload" size={16} />
              Import events from Instagram, a photo, PDF, or Excel
            </li>
            <li>
              <Icon name="User" size={16} />
              Add more details to your profile
              <span className="onboarding-step__hint">(social links, description...)</span>
            </li>
            <li>
              <Icon name="BarChart" size={16} />
              Check out your analytics—see who's watching!
            </li>
          </ul>
        </div>

        {/* Referral tip */}
        <div className="onboarding-step__referral-tip">
          <Icon name="Gift" size={20} />
          <div>
            <strong>Invite friends, earn rewards!</strong>
            <p>Share your referral with fellow organizers—you'll both get 1 month free!</p>
          </div>
        </div>

        {/* Help tip - different text for desktop vs mobile */}
        <div className="onboarding-step__help-tip">
          <Icon name="CircleHelp" size={16} />
          <span>
            Need a guided tour?{' '}
            {isDesktop ? (
              <>Click <strong>Help</strong> in the sidebar anytime!</>
            ) : (
              <>Tap the <strong>menu</strong> and select <strong>Help</strong>!</>
            )}
          </span>
        </div>
      </div>

      {/* CTA */}
      <div className="onboarding-step__actions">
        <Button
          variant="primary"
          size="lg"
          onClick={handleComplete}
          disabled={isSubmitting}
          className="onboarding-step__cta"
        >
          {isSubmitting ? 'Finishing...' : 'Create My First Event'}
          <Icon name="ArrowRight" size={18} />
        </Button>
      </div>
    </div>
  );
};

export default ReadyStep;
